import { cn } from "@/lib/utils";
import apiRoutes from "@/src/apiRoutes";
import { Button } from "@/src/components/Button/Button";
import { Modal } from "@/src/components/Modal/Modal";
import { textVariants } from "@/src/components/Text/Text";
import { Card } from "@/src/components/shad/card";
import { RadioGroup, RadioGroupItem } from "@/src/components/shad/radio";
import { CheckEnablers } from "@/src/hooks/check-enablers";
import { useUserPaymentMethods } from "@/src/hooks/usePaymentMethods";
import api, { removeUndefinedAndNull } from "@/src/utils";
import { useMutation, useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

const DepositSettings = () => {
  const { t } = useTranslation();
  const trans = (key: string) => t(`app.wallet.deposit.settings.cards.${key}`);
  const [open, setOpen] = useState(false);
  const { paymentMethods } = useUserPaymentMethods();
  const [selectedPayment, setSelectedPayment] = useState<number>();
  const { data: modes } = CheckEnablers("deposit_modes");

  const options = [
    {
      label: trans("spot.label"),
      desc: trans("spot.desc"),
      mode: "spot",
    },
    {
      label: trans("sell.label"),
      desc: trans("sell.desc"),
      mode: "sell",
    },
    {
      label: trans("p2p.label"),
      desc: trans("p2p.desc"),
      mode: "p2p",
    },
    {
      label: trans("convert.label"),
      desc: trans("convert.desc"),
      mode: "convert",
    },
  ];
  // const { data, isLoading: checkLoad, refetch } = CheckEnablers("deposit_mode");
  const getEnablers = async () => {
    const res = await api.post(apiRoutes.checkEnablers, {
      type: "deposit_mode",
    });
    return res.data;
  };

  const {
    data,
    isLoading: checkLoad,
    refetch,
  } = useQuery({
    queryKey: [`check_enablers_deposit_mode`],
    queryFn: getEnablers,
    onSuccess: (data) => {
      if (data?.details) {
        setSelectedPayment(data?.details.id);
      }
    },
  });

  const setDeposit = async (mode: string) => {
    // if (mode === "sell") {
    //   setOpen(true);
    //   return;
    // }
    const res = await api.post(
      apiRoutes.settings,
      removeUndefinedAndNull({
        type: "deposit_mode",
        mode,
        payment_id: mode === "sell" ? selectedPayment : undefined,
      })
    );
    return res.data;
  };

  const { mutate, isLoading } = useMutation({
    mutationKey: ["deposit_settings"],
    mutationFn: setDeposit,
    onSuccess: (data) => {
      if (data?.success) {
        toast.success(data?.info);
        setOpen(false);
        refetch();
      } else {
        toast.warning(data?.info);
      }
    },
  });

  if (checkLoad || !modes) return null;
  return (
    <>
      <span className={textVariants({ font: "semibold", size: "lg" })}>
        {trans("header")}
      </span>
      <RadioGroup
        defaultValue="Farmer"
        value={data?.info}
        className="w-full grid md:grid-cols-2 gap-3"
      >
        {modes?.info.map((item: string, index: number) => (
          <div key={index}>
            <Card
              onClick={() => (item === "sell" ? setOpen(true) : mutate(item))}
              className={cn(
                data?.info === item && "border-indigo-400",
                isLoading ? "cursor-wait " : "cursor-pointer",
                "rounded-md py-3 px-5"
              )}
            >
              {/* <input type="radio" checked={selected === item?.label} className="accent-green-200" /> */}
              <div className="flex gap-2 justify-between items-center">
                <div className="grid ">
                  <span
                    className={textVariants({
                      font: "semibold",
                      case: "capitalize",
                    })}
                  >
                    {item}
                  </span>
                  {/* <span className={textVariants({ variant: "desc", size: "xs" })}>
                {item?.desc}
              </span> */}
                </div>
                <div className="flex">
                  <RadioGroupItem
                    checked={data?.info === item}
                    className=" text-indigo-400"
                    value={item}
                    id="r1"
                  />
                </div>
              </div>
              <span className={cn("md:hidden", textVariants({ size: "xs" }))}>
                {/* {item?.desc} */}
                {/* {
                  options.find((thing) => thing.label?.toLowerCase() === item)
                    ?.desc
                } */}
              </span>
            </Card>
          </div>
        ))}
      </RadioGroup>
      <span
        className={cn(
          textVariants({
            size: "sm",
            variant: "desc",
            font: "semibold",
          }),
          "hidden md:flex"
        )}
      >
        {options.find((item) => item?.mode === data?.info)?.desc}
      </span>
      {data?.info === "sell" && (
        <div>
          <Card onClick={() => setOpen(true)} className="cursor-pointer">
            <div
              className={cn(
                textVariants({ size: "sm", font: "semibold" }),
                "py-4 px-3 flex w-full justify-between items-center"
              )}
            >
              <div className="flex items-center gap-2">
                <img
                  src={data?.details?.service__image}
                  className="h-6 w-6 rounded-full"
                  alt={data?.details?.service__name}
                />

                <div className="grid">
                  <span
                    className={textVariants({ size: "sm", font: "semibold" })}
                  >
                    {data?.details?.service__type}
                  </span>
                  <span className={textVariants({ size: "xs" })}>
                    {data?.details?.account_number}
                  </span>
                </div>
              </div>
              {/* <span>Change Sell Payment Method</span> */}
              <ChevronRight className="w-5 h-5" />
            </div>
          </Card>
        </div>
      )}

      <Modal
        open={open}
        setOpen={setOpen}
        setter="state"
        title={trans("sell.modal.title")}
        description={trans("sell.modal.desc")}
        footer={
          <Button
            disabled={!selectedPayment}
            loading={isLoading}
            onClick={() => mutate("sell")}
          >
            {trans("sell.modal.continueButton")}
          </Button>
        }
      >
        <RadioGroup className="grid md:grid-cols-2 gap-2">
          {paymentMethods?.map((item) => (
            <Card
              key={item?.id}
              onClick={() => setSelectedPayment(item?.id)}
              className={cn("flex justify-between items-center px-2")}
            >
              <div className="p-2 flex items-center gap-2 cursor-pointer">
                <div>
                  <img
                    src={item?.service__image}
                    className="h-6 w-6 rounded-full"
                    alt={item?.service__name}
                  />
                </div>
                <div className="grid gap-1">
                  <span className={textVariants({ font: "semibold" })}>
                    {item?.service__name}
                  </span>
                  <span className={textVariants({ size: "xs" })}>
                    {item?.account_number}
                  </span>
                </div>
              </div>
              <div>
                <RadioGroupItem
                  checked={selectedPayment === item?.id}
                  className=" text-indigo-400"
                  value={item?.id.toString()}
                  id="r2"
                />
              </div>
            </Card>
          ))}
        </RadioGroup>
      </Modal>
    </>
  );
};

export default DepositSettings;
